import cloneDeep from 'clone-deep'
import { RepeatType } from 'components/Frequency/constants'
import { LayoutLoading } from 'components/LayoutLoading'
import { useSharedLoan } from 'context/SharedLoanContext'
import { useBankNameLookup } from 'hooks/useBankNameLookup'
import { useEffect, useRef, useState } from 'react'
import { getACH, postACH } from 'services'
import { Tooltip } from 'stories/components'
import { openAuditLog } from 'utils'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import { defaultInputs } from './constants'

const isOverrideMode = (data: Record<string, any>) => {
  if (data.draftAmountMode === 'override') return true
  if (data.draftAmountMode === 'piti' || data.draftAmountMode === 'pitiPlusCharges') return false
  return data.draftAmountOverride === true || String(data.draftAmountOverride ?? '').toUpperCase() === 'Y'
}

/**
 * Routing field -> the institution-name field beside it. Two accounts live on this screen: the one
 * payments are drafted from, and the optional one money paid out to the borrower is deposited into.
 */
const BANK_NAME_BY_ROUTING_KEY: Record<string, string> = {
  accountRoutingNo: 'accountFinancialInsName',
  depositAccountRoutingNo: 'depositAccountFinancialInsName',
}

const ROUTING_KEY_BY_BANK_NAME: Record<string, string> = Object.fromEntries(
  Object.entries(BANK_NAME_BY_ROUTING_KEY).map(([routingKey, nameKey]) => [nameKey, routingKey]),
)

export default function ACH() {
  const shared = useSharedLoan()
  const isShared = !!shared?.readOnly
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<any>({})
  const [orgData, setOrgData] = useState<any>({})
  // A typed date reaches us as one onChange and then an onBlur in the same turn, before React has
  // committed the change, so `data` read out of the blur handler's closure is the value from
  // before the edit. Mirror it into a ref and read that instead. Next Payment Date and
  // Authorization Date are the two fields this bit.
  const dataRef = useRef<any>({})
  useEffect(() => {
    dataRef.current = data
  }, [data])
  // One lookup per account: each keeps its own record of the name it filled in, so auto-filling
  // one bank's name never makes the other's look auto-filled and overwritable.
  const draftBankLookup = useBankNameLookup()
  const depositBankLookup = useBankNameLookup()
  const bankLookups: Record<string, ReturnType<typeof useBankNameLookup>> = {
    accountRoutingNo: draftBankLookup,
    depositAccountRoutingNo: depositBankLookup,
  }
  /**
   * Per routing field, whether the last complete routing number found nothing in the directory.
   * Without this the lookup failing and the lookup never running look identical, which is what
   * made a tenant with no imported directory read as a broken feature.
   */
  const [bankLookupMissed, setBankLookupMissed] = useState<Record<string, boolean>>({})
  const inputs = defaultInputs()

  const isDraftOverride = isOverrideMode(data)

  const reload = async () => {
    const res: any = await getACH().catch(() => null)
    if (res?.success) {
      setData(res.data)
      setOrgData(res.data)
    }
  }

  useEffect(() => {
    if (isShared) {
      const res = shared.pageData as any
      const achData = res?.data ?? res
      if (achData && typeof achData === 'object') {
        setData(achData)
        setOrgData(achData)
      }
      setLoading(false)
      return
    }
    setLoading(true)
    setLoanNumber()
    reload().finally(() => setLoading(false))
  }, [isShared, shared?.pageKey, shared?.pageData])

  /**
   * ACH End Date is stored inside the recurrence object (`data.frequency.endDate`) that older
   * records already carry, so the draft run and the party export keep reading one place. The rest
   * of that object is legacy: the cadence is now the plain `achFrequency` field beside it.
   */
  const onEndDateChange = (value: any) => {
    const frequency = { ...(dataRef.current.frequency || {}), repeatType: RepeatType.Repeat, endDate: value || '' }
    dataRef.current = { ...dataRef.current, frequency }
    setData((current: any) => ({ ...current, frequency }))
    onBlur('frequency', frequency)
  }

  const onChange = async (key: string, value: any) => {
    if (isShared || loading) return
    if (key === 'frequencyEndDate') return onEndDateChange(value)
    const temp = cloneDeep(dataRef.current)
    temp[key] = value
    dataRef.current = temp
    setData(temp)

    const inputType = (inputs[key]?.inputType || '').toLowerCase()
    const saveNow =
      key === 'authorizationForm' ||
      key === 'draftAmountMode' ||
      key === 'draftAmountOverride' ||
      inputType === 'select' ||
      inputType === 'toggle' ||
      inputType === 'filetable'

    if (saveNow) {
      if (JSON.stringify(value) === JSON.stringify(orgData[key])) return
      setLoading(true)
      await postACH({ key, value })
      setOrgData((current: any) => ({ ...current, [key]: value }))
      // Mode changes re-quote the draft amount (PITI / PITI+charges / leave override).
      if (key === 'draftAmountMode' || key === 'draftAmountOverride') await reload()
      setLoading(false)
      return
    }

    const bankNameKey = BANK_NAME_BY_ROUTING_KEY[key]
    if (bankNameKey) {
      // Fill in the institution the routing number belongs to, and save it the same way a typed
      // value would be saved - the field is only posted on blur, and auto-fill never blurs it.
      const bankName = await bankLookups[key].resolveBankName(value, temp[bankNameKey])
      if (bankName) {
        setBankLookupMissed((current) => ({ ...current, [key]: false }))
        dataRef.current = { ...temp, [bankNameKey]: bankName }
        setData(dataRef.current)
        setOrgData((current: any) => ({ ...current, [bankNameKey]: bankName }))
        await postACH({ key: bankNameKey, value: bankName })
      } else {
        // A complete routing number that resolves to nothing is worth saying out loud; an
        // incomplete one is just mid-typing, and a name the user typed themselves is deliberate.
        const digits = String(value ?? '').replace(/\D/g, '')
        const missed = digits.length === 9 && !`${temp[bankNameKey] ?? ''}`.trim()
        setBankLookupMissed((current) => ({ ...current, [key]: missed }))
      }
    }
  }

  const onBlur = async (key: string, _value?: any) => {
    if (isShared) return
    const value = _value !== undefined ? _value : dataRef.current[key]
    if (JSON.stringify(value) === JSON.stringify(orgData[key])) return
    setLoading(true)
    await postACH({
      key,
      value,
    })
    setOrgData((current: any) => ({ ...current, [key]: value }))
    setLoading(false)
  }

  const showHistory = (key: string) => {
    if (isShared) return
    openAuditLog({
      table: 'Ach',
      field: key,
      keys: {
        field: key,
      },
      fieldLabel: inputs[key]?.title || (key === 'frequency' ? 'Payment Frequency' : key),
    })
  }

  return (
    <div className="servicing-note-container">
      <LayoutLoading show={loading} />
      <h2 className="text-2xl flex items-center gap-1 font-bold mb-2">
        <span>ACH</span>
      </h2>
      <div className="w-full">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {Object.keys(inputs).map((key, index) => {
            const input = inputs[key]
            input.value = key === 'frequencyEndDate' ? data.frequency?.endDate || '' : data[key]
            if (key === 'draftAmountMode' && !input.value) {
              input.value = isDraftOverride ? 'override' : 'piti'
            }
            if (isShared) {
              input.readOnly = true
              input.disabled = true
            } else if (key === 'draftAmount') input.readOnly = !isDraftOverride
            const routingKeyForThisName = ROUTING_KEY_BY_BANK_NAME[key]
            if (routingKeyForThisName && !isShared) {
              input.additionalElements = bankLookups[routingKeyForThisName].loadingBankName ? (
                <span className="ml-1 text-[11px] italic text-gray-400">Looking up...</span>
              ) : bankLookupMissed[routingKeyForThisName] ? (
                <Tooltip message="No institution is on file for that routing number. Type the bank name yourself, or import the routing directory under Admin > Accounting > Trust Accounts.">
                  <span className="ml-1 text-[11px] italic text-gray-400">Not in the routing directory</span>
                </Tooltip>
              ) : null
            }

            return (
              <div
                key={index}
                className={
                  input.span === 3 || input.span === 'full'
                    ? 'col-span-1 sm:col-span-3'
                    : input.span === 2
                    ? 'col-span-1 sm:col-span-2'
                    : 'col-span-1'
                }
              >
                <RenderInput
                  input={input}
                  Key={key}
                  onChange={(key: string, value: any) => onChange(key, value)}
                  // The end date saves through the recurrence on change; it has nothing of its
                  // own under `data` for the blur save to compare against.
                  onBlur={(key: string) => key !== 'frequencyEndDate' && onBlur(key)}
                  showHistory={isShared ? undefined : key === 'frequencyEndDate' ? undefined : showHistory}
                />
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
